import { makeObservable, observable, flow, action, computed, IObservableArray, runInAction } from "mobx";
import { AzureBlobStore, NotificationStoreInstance } from "@common/stores";
import { IBlobSasModel, IPetColorModel, IGenderModel, IBreedModel, IPhotoModel, EnsurePetAlertsRequest, ISpeciesModel, IPGRBreedModel, IPetVisitRemindersModel, PetModel, ClientConfigurationModel, IIdexxBreedModel, IPetModel, EstimateModel, EstimateSalesItemModel, MedicalHistoryModel, PetAttachmentModel } from "@common/models";
import { BlobEntityType, DiscountItemTypes, NoteTypes } from "@common/models/Enums";
import { ClientService, PetNoteService, PetService } from "@common/services";
import { CustomerInteractionModel } from "@common/models/CustomerInteractionModel";
import { orderBy } from "lodash";
import { IPetNoteModel } from "@common/models/PetNoteModel";
import { cleanseFirstParagraph } from "@common/utils";
import { filterForQuery } from "@common/components/utility";
import { QuerySearchButtonSetQuery } from "@hospital/components/search";
import { SourceSystem } from "@common/models/Enums/SourceSystem";
import { HospitalPrescriptionListingModel } from "@common/models/HospitalPrescriptionListingModel";

// #region default models
const defaultSpecies: ISpeciesModel = {
	speciesId: 0,
	name: "",
	rowVersion: "",
};

const defaultBreed: IBreedModel = {
	breedId: 0,
	name: "",
	speciesId: 0,
	species: defaultSpecies,
	pgrBreed: {} as IPGRBreedModel,
	idexxBreed: {} as IIdexxBreedModel,
	idexxBreedId: 0,
	pgrBreedId: 0,
	rowVersion: "",
};

const defaultPetColor: IPetColorModel = {
	petColorId: 0,
	name: "",
	pgrColorId: 0,
	pgrColor: {
		pgrColorId: 0,
		code: "",
		type: "",
		sort: "",
		defaultPetpassPetColorId: 0,
		rowVersion: "",
	},
	rowVersion: "",
};

const defaultGender: IGenderModel = {
	genderId: 0,
	name: "",
	rowVersion: "",
};
const defaultPet: IPetModel = {
	petId: 0,
	speciesId: 0,
	name: "",
	isActive: true,
	microchipNumber: undefined,
	clientId: 0,
	alerts: undefined,
	hasAlerts: false,
	isDeceased: false,
	rabiesTagNumber: undefined,
	dateCreated: "",
	dateUpdated: "",
	species: defaultSpecies,
	gender: defaultGender,
	breed: defaultBreed,
	petColor: defaultPetColor,
	petAlerts: [],
	breedFullName: "",
	isMixedBreed: false,
	rowVersion: "",
	notes: [],
	entityMappings: [],
};

// #endregion

export interface IBirthDateFields {
	years: number;
	months: number;
	weeks: number;
}

export interface IPetDetailsStoreInit extends Partial<PetDetailsStore> {
	configuration: ClientConfigurationModel;
}
export class PetDetailsStore {
	configuration!: ClientConfigurationModel;
	blobStore!: AzureBlobStore;

	@observable pet: PetModel = new PetModel(defaultPet);
	@observable petLoading: boolean = false;
	@observable remindersLoading: boolean = false;
	@observable historyLoading: boolean = false;
	@observable private _baseMedicalHistory: IObservableArray<MedicalHistoryModel> = observable.array([]);
	@observable private _medicalHistorySearchQuery: string = "";
	@observable petAttachments: IObservableArray<PetAttachmentModel> = observable([]);
	@observable petappointmentreminders: IPetVisitRemindersModel[] = [];
	@observable petHospitalPrescriptions: HospitalPrescriptionListingModel[] = [];
	@observable estimates: EstimateModel[] = [];
	@observable customerInteractions: CustomerInteractionModel[] = [];
	@observable pendingPet?: PetModel;

	constructor(init: IPetDetailsStoreInit) {
		Object.assign(this, init);

		if (!this.blobStore) this.blobStore = new AzureBlobStore({ containerName: BlobEntityType.Pet });

		makeObservable(this, undefined, { autoBind: true });
	}

	@computed get getSpecies() {
		return this.configuration.species;
	}

	@computed get getBreeds() {
		return this.configuration.breeds;
	}

	@computed get getGenders() {
		return this.configuration.genders;
	}

	@computed get getColors() {
		return this.configuration.petColors;
	}

	@computed get loading() {
		return this.petLoading || this.historyLoading || this.remindersLoading;
	}

	@action.bound setPet(pet: PetModel) {
		this.pet = pet;
	}

	@action.bound clearPet() {
		this.pet = new PetModel(defaultPet);
	}

	@computed get medicalHistorySearchQuery() {
		return this._medicalHistorySearchQuery;
	}
	/**
	 * When _baseMedicalHistory changes from fetchPetMedicalHistory or pet changes, this computed
	 * will recalculate the pet note medical history items that need to be added to the _baseMedicalHistoryArray
	 * and the concatenated array containing both is returned.  This keeps the array in sync with the loaded pet and medical history.
	 */
	@computed get medicalHistory() {
		const medicalHistoryNotes = this.pet.notes
			.filter((a: IPetNoteModel) => a.noteTypeId === NoteTypes.Vaccination || a.noteTypeId === NoteTypes.Medication)
			.map(note => {
				return new MedicalHistoryModel({
					name: note.notes,
					fullName: cleanseFirstParagraph(note.notes),
					salesDate: note.dateCreated,
					isNote: true,
					creator: note.createdUserFirstName === undefined || note.createdUserFirstName == "" ? "Unknown" : `${note.createdUserFirstName} ${note.createdUserLastName}`,
				});
			});
		return _.concat(this._baseMedicalHistory, medicalHistoryNotes);
	}

	@computed get nonDiscountMedicalHistory() {
		return this.medicalHistory.filter(ph => !DiscountItemTypes.includes(ph.salesItemSalesItemTypeId ?? 0));
	}

	@computed get filteredNonDiscountMedicalHistory() {
		let filtered: MedicalHistoryModel[] = [];
		if (this._medicalHistorySearchQuery) {
			filtered = filterForQuery(this._medicalHistorySearchQuery, this.nonDiscountMedicalHistory, ["veterinarianName", "fullName", "price", "creator", "storeCode", "hospitalName", "salesDate"]);
		} else filtered = this.nonDiscountMedicalHistory;
		return orderBy(filtered, [a => new Date(a.salesDate), "row"], ["desc", "asc"]);
	}

	@computed get vetspireMedicalDocuments() {
		return this.pet.entityMappings.filter(entityMapping => entityMapping.sourceSystemId === SourceSystem.VetSpire);
	}

	@action.bound setDates(pet: PetModel) {
		if (!pet.dateCreated) {
			pet.dateCreated = "01-01-0001";
		}
		if (!pet.dateUpdated) {
			pet.dateUpdated = "01-01-0001";
		}
	}

	@action.bound setPendingPet(pendingPet: PetModel) {
		if (pendingPet === undefined) return;
		this.pendingPet = pendingPet;
	}

	@action.bound setMedicalHistorySearchQuery: QuerySearchButtonSetQuery = (query: string) => {
		this._medicalHistorySearchQuery = query ?? "";
	};

	savePetDetails = flow(function* (this: PetDetailsStore, pet: PetModel, clientId: string, refetchPet: boolean = true) {
		this.petLoading = true;
		pet.clientId = parseInt(clientId);

		if (pet.petId == 0) {
			this.setDates(pet);

			pet.isActive = true;

			const petPhoto: IPhotoModel | undefined = pet.photo;
			const newPet: PetModel = yield PetService.addPet(pet).catch(() => NotificationStoreInstance.onError("Failed to add pet.", undefined));

			if (petPhoto && newPet && newPet.petId > 0) {
				yield this.savePetPhoto(newPet.petId, undefined, petPhoto);
			}

			if (refetchPet) {
				yield this.fetchPet(newPet.petId);
			} else {
				this.pet.petId = newPet.petId;
			}
		} else {
			yield PetService.updatePet(pet).catch(() => NotificationStoreInstance.onError("Failed to update pet info.", undefined));

			if (refetchPet) {
				yield this.fetchPet(pet.petId);
			}
		}

		this.petLoading = false;
	});

	savePetPhoto = flow(function* (this: PetDetailsStore, petId: number, petPhotoFile?: File, petPhoto?: IPhotoModel) {
		//this happens on changePetPhotoModal, when a photo is being changed or created
		if (petPhotoFile) {
			//photo has to be uploaded to azure before it can go anywhere else
			const blobSas: IBlobSasModel = yield this.blobStore.uploadFile(petPhotoFile);
			blobSas.modelId = petId;
			if (blobSas) {
				if (petId === 0) {
					//new pet just update petPhoto observable property, will be used from savePetDetails
					if (this.pet) {
						this.pet = {
							...this.pet,
							photo: {
								container: blobSas.container,
								fileName: blobSas.fileName,
								photoUrl: blobSas.sasUrl,
								photoId: 0,
								processedPhotoBaseUrl: "",
							},
						};
					}
				} else {
					yield PetService.updatePetPhoto(petId, blobSas).catch(() => NotificationStoreInstance.onError("Failed to update pet photo.", undefined));

					//fetch a new pet because photo isn't observable in IPetModel
					yield this.fetchPet(petId);
				}
			}
		} else {
			//this happens when save is calld from savePetDetails flow, photos have to be saved after the pet
			//exists or we need to add code to pet post to handle the photo on the pet model
			if (petPhoto) {
				const blobSas: IBlobSasModel = {
					modelTypeName: "",
					issueDate: "",
					container: petPhoto.container,
					modelId: petId,
					fileName: petPhoto.fileName,
					sasUrl: petPhoto.photoUrl,
					sasToken: "",
				};

				const photoObject = yield PetService.updatePetPhoto(petId, blobSas).catch(() => NotificationStoreInstance.onError("Failed to update pet photo.", undefined));

				if (this.pet) {
					this.pet = { ...this.pet, photo: photoObject };
				}
			}
		}
		return;
	});

	loadPetDetails = flow(function* (this: PetDetailsStore, petId: number) {
		yield Promise.all([this.fetchPet(petId), this.fetchPetReminder(petId), (this.estimates = [])]);
	});

	fetchPetMedicalHistory = flow(function* (this: PetDetailsStore, clientId: number, petId: number) {
		const medicalHistory: MedicalHistoryModel[] = yield ClientService.getClientMedicalHistoryForPet(clientId, petId, { showError: true });
		this._baseMedicalHistory.clear();
		medicalHistory.forEach(h => this._baseMedicalHistory.push(h));
	});

	fetchPetAttachments = flow(function* (this: PetDetailsStore, petId: number) {
		if (petId) {
			this.petAttachments = yield PetService.getAttachments(petId);
		}
	});

	@action.bound setPetMedicalHistory(medicalHistory: MedicalHistoryModel[]) {
		this._baseMedicalHistory.clear();
		medicalHistory.forEach(history => this._baseMedicalHistory.push(history));
	}

	fetchPet = flow(function* (this: PetDetailsStore, id: number | undefined) {
		this.petLoading = true;
		this._medicalHistorySearchQuery = "";
		if (!id) {
			this.pet = new PetModel(defaultPet);
		} else {
			this.pet = yield PetService.fetchPet(id);
		}
		this.petLoading = false;
	});

	fetchPetReminder = flow(function* (this: PetDetailsStore, petId: number) {
		this.remindersLoading = true;
		if (petId) {
			this.petappointmentreminders = yield PetService.getVisitReminders(petId).catch(() => NotificationStoreInstance.onError("Failed to fetch pet reminders.", []));
		}

		this.remindersLoading = false;
	});

	fetchPetHospitalPrescriptions = flow(function* (this: PetDetailsStore, petId: number) {
		if (petId) {
			this.petHospitalPrescriptions = yield PetService.getHospitalPrescriptions(petId).catch(() => NotificationStoreInstance.onError("Failed to fetch pet prescriptions.", []));
		}
	});

	savePetAlerts = flow(function* (this: PetDetailsStore, petId: number, petAlerts: number[]) {
		let request = new EnsurePetAlertsRequest({ petId: petId, PetAlertIds: petAlerts });

		yield PetService.ensurepetalerts(petId, request).catch(() => NotificationStoreInstance.onError("Failed to update pet alerts.", undefined));

		yield this.fetchPet(petId);

		return;
	});

	public fetchEstimates = flow(function* (this: PetDetailsStore, petId: number) {
		this.estimates = yield PetService.fetchEstimates(petId).catch(NotificationStoreInstance.onError("Failed to fetch estimates for pet", this.estimates));
	});

	public createEstimate = flow(function* (this: PetDetailsStore, clinicId: number, timezone: string | undefined) {
		if (this.pet === undefined || timezone === undefined) return;
		const petId = this.pet.petId;
		this.estimates = yield PetService.createEstimate(clinicId, petId, this.pet.name, timezone)
			.then(() => PetService.fetchEstimates(petId))
			.catch(NotificationStoreInstance.onError("Failed to create estimate", this.estimates));
	});

	public resetPet = () => {
		this._baseMedicalHistory.clear();
		this._medicalHistorySearchQuery = "";
		this.pet = new PetModel(defaultPet);
		this.petappointmentreminders = [];
		this.estimates = [];
		this.customerInteractions = [];
	};
	public resetPetMedicalDetails = () => {
		runInAction(() => {
			this.pet = new PetModel(defaultPet);
			this._baseMedicalHistory.clear();
			this.petHospitalPrescriptions = [];
			this.customerInteractions = [];
			this.petAttachments.clear();
		});
	};

	public updateEstimate = flow(function* (this: PetDetailsStore, clinicId: number, estimate: EstimateModel) {
		this.estimates = yield PetService.updateEstimate(clinicId, estimate)
			.then(() => PetService.fetchEstimates(estimate.petId))
			.catch(NotificationStoreInstance.onError("Failed to update estimate name", this.estimates));
	});

	public addEstimateSalesItem = flow(function* (this: PetDetailsStore, estimate: EstimateModel, visitId: number, salesItemOptionId: number) {
		this.estimates = yield PetService.addEstimateSalesItem(estimate.petId, estimate.estimateId, visitId, salesItemOptionId)
			.then(() => PetService.fetchEstimates(estimate.petId))
			.catch(NotificationStoreInstance.onError("Failed to add sales item to estimate", this.estimates));
	});

	public updateEstimateSalesItem = flow(function* (this: PetDetailsStore, estimateSalesItem: EstimateSalesItemModel) {
		if (this.pet === undefined) return;
		const petId = this.pet.petId;
		this.estimates = yield PetService.updateEstimateSalesItem(petId, estimateSalesItem)
			.then(() => PetService.fetchEstimates(petId))
			.catch(NotificationStoreInstance.onError("Failed to update estimate sales item", this.estimates));
	});

	public deleteEstimateSalesItem = flow(function* (this: PetDetailsStore, estimateSalesItem: EstimateSalesItemModel) {
		if (this.pet === undefined) return;
		const petId = this.pet.petId;
		this.estimates = yield PetService.deleteEstimateSalesItem(petId, estimateSalesItem.estimateId, estimateSalesItem.salesItemId)
			.then(() => PetService.fetchEstimates(petId))
			.catch(NotificationStoreInstance.onError("Failed to remove estimate sales item", this.estimates));
	});

	public addEstimateSalesPackage = flow(function* (this: PetDetailsStore, estimate: EstimateModel, visitId: number, salesPackageId: number) {
		this.estimates = yield PetService.addEstimateSalesPackage(estimate.petId, estimate.estimateId, visitId, salesPackageId)
			.then(() => PetService.fetchEstimates(estimate.petId))
			.catch(NotificationStoreInstance.onError("Failed to add sales package to estimate", this.estimates));
	});

	public deleteEstimateSalesPackage = flow(function* (this: PetDetailsStore, estimate: EstimateModel) {
		this.estimates = yield PetService.deleteEstimateSalesPackage(estimate.petId, estimate.estimateId)
			.then(() => PetService.fetchEstimates(estimate.petId))
			.catch(NotificationStoreInstance.onError("Failed to remove sales package from estimate", this.estimates));
	});

	public addEstimateSalesPlan = flow(function* (this: PetDetailsStore, estimate: EstimateModel, visitId: number, salesPlanId: number) {
		this.estimates = yield PetService.addEstimateSalesPlan(estimate.petId, estimate.estimateId, visitId, salesPlanId)
			.then(() => PetService.fetchEstimates(estimate.petId))
			.catch(NotificationStoreInstance.onError("Failed to add sales package to estimate", this.estimates));
	});

	public fetchCustomerInteractions = flow(function* (this: PetDetailsStore, clientId: number, petId: number) {
		this.customerInteractions = yield ClientService.getClientInteractions(clientId)
			.then(customerInteractions =>
				orderBy(
					customerInteractions.filter((interaction: CustomerInteractionModel) => Number(interaction.petId) === petId),
					"clientInteractionId",
					"desc"
				)
			)
			.catch(NotificationStoreInstance.onError("Failed to fetch customer interaction", this.customerInteractions));
	});

	public upsertCustomerInteraction = flow(function* (this: PetDetailsStore, customerInteraction: Partial<CustomerInteractionModel>) {
		customerInteraction.clientInteractionId === 0
			? yield ClientService.addCustomerInteraction(customerInteraction)
				.then(() => this.fetchCustomerInteractions(customerInteraction.clientId!, customerInteraction.petId!))
				.catch(NotificationStoreInstance.onError("Failed to create customer interaction", customerInteraction))
			: yield ClientService.updateCustomerInteraction(customerInteraction)
				.then(() => this.fetchCustomerInteractions(customerInteraction.clientId!, customerInteraction.petId!))
				.catch(NotificationStoreInstance.onError("Failed to update customer interaction", customerInteraction));
	});

	public deleteCustomerInteraction = flow(function* (this: PetDetailsStore, clientInteractionId: number) {
		yield ClientService.deleteCustomerInteraction(clientInteractionId)
			.then(() => this.fetchCustomerInteractions(this.pet.clientId, this.pet.petId))
			.catch(NotificationStoreInstance.onError("Failed to delete customer interaction", clientInteractionId));
	});

	public deleteEstimate = flow(function* (this: PetDetailsStore, estimate: EstimateModel) {
		this.estimates = yield PetService.deleteEstimate(estimate.estimateId)
			.then(() => PetService.fetchEstimates(estimate.petId))
			.then(NotificationStoreInstance.onSuccess("Estimate successfully deleted."))
			.catch(NotificationStoreInstance.onError("Failed to delete estimate.", this.estimates));
	});

	public addPetAttachments = flow(function* (this: PetDetailsStore, files: FileList, documentTypeId: number, notes: string | undefined, hospitalId: number, setProgress: (total: number, completed: number, errored: number) => void) {
		// initialize the progress bar
		let completed = 0;
		let errored = 0;
		setProgress(files.length, completed, errored);

		// post each file blob to azure storage, then create an attachment.
		const blobStore = new AzureBlobStore({ containerName: BlobEntityType.Attachment });
		for (let i = 0; i < files.length; i++) {
			try {
				// upload the file to azure blob storage...
				const blobSasModel = yield blobStore.uploadFile(files[i]);
				// ...and immediately create a pet attachment record for the file
				yield PetService.addAttachment(this.pet.petId, hospitalId, blobSasModel, documentTypeId, notes);
				completed++;
			} catch (e) {
				NotificationStoreInstance.error(`Failed to upload ${files[i]}. Please try again.`);
				errored++;
			}
			// update the progress indicator
			setProgress(files.length, completed, errored);
		}
		this.petAttachments = yield PetService.getAttachments(this.pet.petId);
	});

	public upsertPetNote = flow(function* (this: PetDetailsStore, note: IPetNoteModel) {
		yield PetNoteService.upsertPetNote(note).catch(NotificationStoreInstance.onError("Failed to upsert Pet Note", note));

		yield this.fetchPet(note.petId);
	});

	public deletePetAttachments = flow(function* (this: PetDetailsStore, petAttachmentIds: number[]) {
		const successMsg = petAttachmentIds.length > 1 ? "Attachments deleted" : "Attachment deleted";
		const errorMsg = petAttachmentIds.length > 1 ? "Failed to delete some attachments" : "Failed to delete attachment";

		const deletePromises = petAttachmentIds.map(id => PetService.deleteAttachment(this.pet.petId, id));
		yield Promise.all(deletePromises)
			.then(() => NotificationStoreInstance.success(successMsg))
			.catch(NotificationStoreInstance.onError(errorMsg, {}));
		this.petAttachments = yield PetService.getAttachments(this.pet.petId);
	});
}
